import * as React from 'react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { cn } from '../../lib/cn';

/**
 * Generic table for module bottom-list / drill rows. TanStack-powered.
 * Sorting + simple pagination + optional filter input.
 *
 * PII masking happens at the SQL layer (per brief guardrail #8) — the
 * table itself does no further redaction.
 */

export interface DataTableProps<TData, TValue> {
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  /** Caption / heading rendered above the table. */
  caption?: React.ReactNode;
  /** Show a search input that filters across all columns. Default false. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Page size; default 10. Set to 0 to disable pagination. */
  pageSize?: number;
  emptyMessage?: string;
  className?: string;
}

export function DataTable<TData, TValue>({
  data,
  columns,
  caption,
  searchable = false,
  searchPlaceholder = 'Search…',
  pageSize = 10,
  emptyMessage = 'No rows.',
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [filter, setFilter] = React.useState('');

  const filteredData = React.useMemo(() => {
    if (!searchable || !filter.trim()) return data;
    const q = filter.toLowerCase();
    return data.filter((row) => Object.values(row as Record<string, unknown>).some((v) => String(v ?? '').toLowerCase().includes(q)));
  }, [data, filter, searchable]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pageSize > 0 ? getPaginationRowModel() : undefined,
    initialState: pageSize > 0 ? { pagination: { pageSize } } : undefined,
  });

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface overflow-hidden', className)}>
      {(caption || searchable) && (
        <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
          {caption ? (
            typeof caption === 'string' ? (
              <h3 className="text-h3 text-text-primary">{caption}</h3>
            ) : (
              caption
            )
          ) : (
            <span />
          )}
          {searchable && (
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 rounded-md border border-border bg-bg-app px-3 text-caption text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
            />
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-caption">
          <thead className="bg-bg-elevated text-text-secondary">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-border">
                {hg.headers.map((h) => {
                  const sortDir = h.column.getIsSorted();
                  const canSort = h.column.getCanSort();
                  return (
                    <th
                      key={h.id}
                      onClick={canSort ? h.column.getToggleSortingHandler() : undefined}
                      className={cn(
                        'px-4 py-2 text-left font-medium uppercase tracking-wide text-[11px]',
                        canSort && 'cursor-pointer select-none hover:text-text-primary',
                      )}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {canSort &&
                          (sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '')}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-text-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-b-0 hover:bg-bg-elevated/40">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-2 text-text-primary">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pageSize > 0 && table.getPageCount() > 1 && (
        <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-2 text-caption text-text-secondary">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} · {filteredData.length} rows
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="rounded border border-border bg-bg-surface px-2 py-1 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="rounded border border-border bg-bg-surface px-2 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
